import { FC } from "react";
import * as _ from "lodash";
import { Link, withRouter, RouteComponentProps, useRouteMatch } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  Menu,
  Badge,
  AppstoreOutlined,
  CompassOutlined,
  ClockCircleOutlined,
  LineChartOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  BulbOutlined,
  MessageOutlined,
  MailOutlined,
  InfoCircleOutlined,
  ExperimentOutlined,
  Icon,
  Copilot,
  Row,
  Col,
  PlayCircleOutlined,
  Button
} from "@ctra/components";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { useGoogleAnalytics } from "@ctra/analytics";
import { classname } from "@ctra/utils";
import { BreadcrumbList, EnterpriseAppState, Session, Enterprise as App } from "@ctra/api";

import { OnboardingCSSClass, useDataIntegration, useIntercom, useOnboarding } from "@base";
import { useFarmList } from "@farms";
import { Routes } from "@routes";

import { GAActions as AuthGAActions } from "../../../auth/analytics";
import { GAActions } from "../../analytics";
import { intercomLauncherClassName } from "../Intercom";

import styles from "./Navigation.module.less";
import { useCurrentUser } from "@auth";

type NavigationProps = RouteComponentProps & {
  /**
   * callback for toggling the collapsed state
   * @param {boolean} collapsed
   */
  onToggle?: (collapsed: boolean) => void;
  /**
   * collapsed state of the navigation controlled by sider
   */
  collapsed?: boolean;
};

/**
 * Main sidebar navigation
 * @type {React.ComponentClass<Omit<NavigationProps, keyof RouteComponentProps<any>> & WithRouterProps<React.FC<NavigationProps>>> & WithRouterStatics<React.FC<NavigationProps>>}
 */
export const Navigation = withRouter<NavigationProps, FC<NavigationProps>>(
  ({ location: { pathname }, collapsed, onToggle = () => void 0 }) => {
    const { t } = useTranslation();
    const { trackEvent } = useGoogleAnalytics();
    const { isChatOpen, unreadCount } = useIntercom();
    const dispatch = useDispatch();
    const { onboardedModules, setOnboardedModules } = useOnboarding();
    const { farmList } = useFarmList();

    const {
      meta: { isAdmin }
    } = useCurrentUser();

    // const {
    //   meta: { dataIntegrated }
    // } = useDataIntegration();

    const dataIntegrated = _.size(farmList) > 0;

    /**
     * Get the breadcrumbs for GA
     * @type {Breadcrumbs}
     */
    const breadcrumbs = useSelector<EnterpriseAppState, BreadcrumbList>(App.entities.getBreadcrumbs);

    /**
     * Route for settings item, used for deep navigation highlighting
     * e.g /settings/insights
     */
    const settings = useRouteMatch(`${Routes.app.settings.index}*`);
    const gamePlans = useRouteMatch(`${Routes.app.gamePlans.index}*`);
    const iofc = useRouteMatch(`${Routes.app.iofc.index}*`);
    const farmSummaries = useRouteMatch(`${Routes.app.farmSummaries.index}*`);
    const timeline = useRouteMatch(`${Routes.app.timeline.index}*`);
    const analytics = useRouteMatch(`${Routes.app.analytics.index}*`);
    const overview = useRouteMatch(`${Routes.app.overview.index}*`);

    return (
      <>
        <Menu
          mode="inline"
          selectedKeys={[pathname]}
          className={classname(styles.Wrapper, OnboardingCSSClass.navigation)}
          selectable={false}
          triggerSubMenuAction="click"
          disabled={_.isEmpty(farmList)}
          style={{ display: "flex", flexDirection: "column", flex: "1 1 auto" }}
          inlineIndent={14}
        >
          <Menu.Item icon={<AppstoreOutlined />} key={Routes.app.overview.index}>
            <Link
              to={Routes.app.overview.index}
              onClick={() => {
                trackEvent(GAActions.goToOverview);
              }}
            >
              {t<string>(Content.navigation.main.overview)}
            </Link>
          </Menu.Item>
          {isAdmin || dataIntegrated ? (
            <>
              <Menu.Item icon={<BulbOutlined />} key={Routes.app.insights.index}>
                <Link
                  to={Routes.app.insights.index}
                  onClick={() => {
                    trackEvent(GAActions.goToInsights);
                  }}
                >
                  {t<string>(Content.navigation.main.insights)}
                </Link>
              </Menu.Item>
              <Menu.Item icon={<LineChartOutlined />} key={analytics?.url || "analytics"}>
                <Link
                  to={Routes.app.analytics.index}
                  onClick={() => {
                    trackEvent(GAActions.goToAnalytics);
                  }}
                >
                  {t<string>(Content.navigation.main.analytics)}
                </Link>
              </Menu.Item>
              <Menu.Item icon={<ClockCircleOutlined />} key={timeline?.url || "timeline"}>
                <Link
                  to={Routes.app.timeline.index}
                  onClick={() => {
                    trackEvent(GAActions.goToTimeline);
                  }}
                >
                  {t<string>(Content.navigation.main.timeline)}
                </Link>
              </Menu.Item>
              <Menu.Item icon={<CompassOutlined />} key={gamePlans?.url || "game-plans"}>
                <Link
                  to={Routes.app.gamePlans.index}
                  onClick={() => {
                    trackEvent(GAActions.goToGamePlans);
                  }}
                >
                  {t<string>(Content.navigation.main.gamePlans)}
                </Link>
              </Menu.Item>
              {isAdmin ? (
               <Menu.Item icon={<ExperimentOutlined />} key={iofc?.url || "iofc"}>
                 <Link
                   to={Routes.app.iofc.index}
                   onClick={() => {
                     trackEvent(GAActions.goToIOFC);
                   }}
                 >
                   {t<string>(Content.navigation.main.iofc)}
                 </Link>
                </Menu.Item>
              ) : null}
              <Menu.Item icon={<Icon component={Copilot} />} key={farmSummaries?.url || "farm-summaries"}>
                <Link
                  to={Routes.app.farmSummaries.index}
                  onClick={() => {
                    trackEvent(GAActions.goToSummaries);
                  }}
                >
                  {t<string>(Content.navigation.main.farmSummaries)}
                </Link>
              </Menu.Item>
            </>
          ) : null}
          <Menu.Item
            className={styles.Collapse}
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => onToggle(!collapsed)}
          >
            {t<string>(Content.navigation.main.collapse, {
              status: collapsed ? "collapsed" : "expanded"
            })}
          </Menu.Item>
        </Menu>

        <Menu
          mode="inline"
          selectedKeys={[pathname]}
          className={classname(styles.Wrapper, OnboardingCSSClass.navigation)}
          selectable={false}
          triggerSubMenuAction="click"
          style={{ marginTop: "auto" }}
          inlineIndent={14}
        >
          <Menu.Divider />
          {overview && (isAdmin || dataIntegrated) ? (
            <Row justify="center" align="middle" className={styles.Tour}>
              <Col>
                <Button
                  shape={collapsed ? "circle" : "default"}
                  icon={<PlayCircleOutlined />}
                  type="secondary"
                  onClick={() => {
                    const onboardingPath = pathname.split("/").slice(0, 3).join("/");

                    trackEvent(GAActions.launchProductTour, {
                      label: _.map(breadcrumbs, "title").join(" / ")
                    });

                    /**
                     * Relaunch tour by setting the visited modules to an empty object
                     */
                    setOnboardedModules({
                      ...onboardedModules,
                      [onboardingPath]: false
                    });
                  }}
                  block={!!collapsed}
                >
                  {t<string>(Content.onboarding.relaunch.action, {
                    variant: collapsed ? "short" : "extended"
                  })}
                </Button>
              </Col>
            </Row>
          ) : null}
          {/** settings?.url could return undefined which gives a warning in the console */}
          {isAdmin || dataIntegrated ? (
            <Menu.Item icon={<SettingOutlined />} key={settings?.url || "settings"}>
              <Link
                to={Routes.app.settings.insights.index}
                onClick={() => {
                  /* istanbul ignore next */
                  trackEvent(GAActions.goToSettings);
                }}
              >
                {t<string>(Content.navigation.main.settings)}
              </Link>
            </Menu.Item>
          ) : null}
          {t<string>(Content.navigation.main.helpCenter) && (
            <Menu.Item icon={<InfoCircleOutlined />} key={Routes.app.helpCenter.index}>
              <a
                href="https://intercom.help/connecterra"
                rel="noopener noreferrer"
                onClick={() => {
                  window.open("https://intercom.help/connecterra", "_blank");
                  /* istanbul ignore next */
                  trackEvent(GAActions.goToHelpCenter);
                }}
                target={"_blank"}
              >
                {t<string>(Content.navigation.main.helpCenter)}
              </a>
            </Menu.Item>
          )}
          <Menu.Item
            className={classname(intercomLauncherClassName, isChatOpen ? styles.Chat : styles.ChatUnselected)}
            icon={
              <Badge count={unreadCount}>
                <MessageOutlined />
              </Badge>
            }
            key={"chat"}
          >
            {t<string>(Content.navigation.main.support)}
          </Menu.Item>
          <Menu.Item
            onClick={() => {
              dispatch(Session.actions.logout());
              /* istanbul ignore next */
              trackEvent(AuthGAActions.logout);
            }}
            icon={<LogoutOutlined />}
          >
            {t<string>(Content.navigation.main.logout)}
          </Menu.Item>
        </Menu>
      </>
    );
  }
);
